<!--
SubscriptionDetailModal — the ONE house of a subscription, for both audiences.

Opened from two places with the SAME event, `modal.subscriptionDetail.open`:
  · /minhas-assinaturas (franqueado) — the row. Three tabs: the subscription's
    data, its transactions, and every subscription this franchise has had.
  · /config/assinaturas (staff) — same event plus `staff: true` and the roster
    row. Every field, grouped, and the two actions that act on the subscription
    itself: Estender and Cancelar.

S12/M3d originally shipped the staff half as a SECOND component with a
hand-rolled field grid and its own CSS. It duplicated this modal and looked
nothing like the rest of the app. Both halves now render through the same house
components (<box> + <compact-data-table>), so the audiences differ in WHICH
fields they see and WHAT they may do — never in how it looks.

The modal RENDERS THE ROW IT WAS HANDED — it never re-fetches. The caller's list
already carries the whole subscription, and a second read would be a second
opinion about the same truth. (Transactions are the one exception: not in the
row, loaded lazily when the franqueado opens that tab.)

Empty fields render "—". NULL means unknown, never zero and never a guess: a
blank "Dias restantes" on an active subscription is correct until the next cycle
payment writes the period.
-->
<template>
  <modal
    elem-id="subscription-detail"
    :show="isVisible"
    :size="isStaff ? 'modalMd' : undefined"
    :title="modalTitle"
    :close-label="t('Fechar')"
    @close="closeModal"
    @dismiss="closeModal"
  >
    <template v-if="isVisible">
      <div data-testid="subscription-detail-modal">
        <!-- ── STAFF: the actions that act on the subscription itself ──────
             Above the tabs (user decision 2026-08-12): the actions apply to
             the SUBSCRIPTION, not to whichever tab happens to be open, and
             below-the-tabs they sat under a variable-height body. -->
        <template v-if="isStaff">
          <div class="actions-section actions-section--top">
            <app-button
              :label="t('Estender')"
              icon="pi pi-calendar-plus"
              severity="secondary"
              outlined
              size="small"
              data-testid="staff-subscription-detail-extend"
              @click="openExtend"
            />
            <app-button
              :label="t('Cancelar assinatura')"
              icon="pi pi-ban"
              severity="danger"
              size="small"
              :disabled="!isCancellable || isCancelling"
              data-testid="staff-subscription-detail-cancel"
              @click="askCancel"
            />
            <app-button
              :label="t('Processar agora')"
              icon="pi pi-bolt"
              severity="secondary"
              outlined
              size="small"
              :disabled="isProcessing || effectiveSubscriptionId === null"
              data-testid="staff-subscription-detail-process"
              @click="handleProcess"
            />
            <app-button
              :label="t('Taxa de implantação')"
              icon="pi pi-dollar"
              severity="secondary"
              outlined
              size="small"
              data-testid="staff-subscription-detail-setup-fee"
              @click="openSetupFee"
            />
          </div>


          <inline-message
            v-if="!isCancellable"
            severity="info"
            data-testid="staff-subscription-detail-cancel-blocked"
          >
            {{ cancelBlockedReason }}
          </inline-message>

          <box
            v-if="confirmingCancel"
            type="danger"
            icon="pi pi-exclamation-triangle"
            :title="t('Confirmar cancelamento')"
            elem-id="boxSubscriptionCancelConfirm"
          >
            <div data-testid="staff-subscription-detail-confirm">
              <p class="confirm-text">
                {{ t('Cancelar é definitivo: não há reativação e a franquia precisa assinar de novo. O cancelamento é feito primeiro no gateway.') }}
              </p>
              <div class="actions-section">
                <app-button
                  :label="t('Confirmar cancelamento')"
                  icon="pi pi-ban"
                  severity="danger"
                  size="small"
                  :disabled="isCancelling"
                  data-testid="staff-subscription-detail-confirm-cancel"
                  @click="handleCancel"
                />
                <app-button
                  :label="t('Voltar')"
                  severity="secondary"
                  outlined
                  size="small"
                  :disabled="isCancelling"
                  data-testid="staff-subscription-detail-abort-cancel"
                  @click="confirmingCancel = false"
                />
              </div>
            </div>
          </box>
        </template>

        <!--
          The SAME three tabs for both audiences. Only the first tab's contents
          differ: a franqueado gets the ten fields that concern them, staff get
          every field, grouped. Tabs 2 and 3 are identical questions ("what was
          charged?", "what else has this franchise had?") and are answered from
          whichever list the caller already holds — see franchiseRows.
        -->
        <Tabs v-model:value="activeTab" :scrollable="false">
          <TabList>
            <Tab :value="0" data-testid="subscription-detail-tab-dados">{{ t('Dados da assinatura') }}</Tab>
            <Tab :value="1" data-testid="subscription-detail-tab-transacoes">{{ t('Transações') }}</Tab>
            <Tab :value="2" data-testid="subscription-detail-tab-todas">{{ t('Todas as assinaturas') }}</Tab>
            <Tab
              v-if="showTestClockTab"
              :value="3"
              data-testid="test-clock-tab"
            >
              {{ t('Relógio') }}
              <span v-if="testClockOffsetDaysTotal > 0"> (+{{ testClockOffsetDaysTotal }}d)</span>
            </Tab>
          </TabList>

          <TabPanels>
            <!-- Tab 1: Subscription data from the row (no refetch) -->
            <TabPanel :value="0" data-testid="subscription-detail-panel-dados">
              <template v-if="isStaff">
                <box
                  v-for="group in staffGroups"
                  :key="group.testId"
                  type="primary"
                  :title="group.title"
                  :elem-id="`boxSubscriptionDetail-${group.testId}`"
                >
                  <div :data-testid="`staff-subscription-detail-group-${group.testId}`">
                    <compact-data-table :value="group.rows" />
                  </div>
                </box>
              </template>
              <compact-data-table v-else :value="dadosRows" />
            </TabPanel>

            <!-- Tab 2: Transactions for this subscription -->
            <TabPanel :value="1" data-testid="subscription-detail-panel-transacoes">
              <data-table
                :config="transacoesConfig"
                :data="txRows"
                data-testid="subscription-detail-transactions-table"
              />
            </TabPanel>

            <!-- Tab 3: all subscriptions for THIS franchise -->
            <TabPanel :value="2" data-testid="subscription-detail-panel-todas">
              <data-table
                :config="todasConfig"
                :data="franchiseRows"
                data-testid="subscription-detail-all-table"
              />
            </TabPanel>

            <!-- Tab 4 (staff only, gated): the GatewayTest clock -->
            <TabPanel v-if="showTestClockTab" :value="3" data-testid="subscription-detail-panel-relogio">
              <subscription-test-clock-tab
                :subscription-id="effectiveSubscriptionId"
                @refresh="handleTestClockRefresh"
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../eventBus'
import { log } from '../../utils/DebugLogger'
import { useMySubscriptions } from '../../composables/useMySubscriptions'
import { permissionsState } from '../../services/permissionsService'
import { useSubscriptionTransactions } from '../../composables/useSubscriptionTransactions'
import { useSubscriptionAdmin } from '../../composables/useSubscriptionAdmin'
import { useDateFormatter } from '../../composables/useDateFormatter'
import { useTestClock } from '../../composables/useTestClock'
import { usePaymentGatewayRegistry } from '../../composables/usePaymentGatewayRegistry'
import Box from '../../ui/Box.vue'
import AppButton from '../../ui/AppButton.vue'
import InlineMessage from '../../ui/InlineMessage.vue'
import CompactDataTable from '../../grid/CompactDataTable.vue'
import SubscriptionTestClockTab from './SubscriptionTestClockTab.vue'
import type { Subscriptions_SubscriptionRow } from '../../contracts/Subscriptions_SubscriptionRow'
import type { Subscriptions_AdminSubscriptionRow } from '../../contracts/Subscriptions_AdminSubscriptionRow'

interface ModalPayload extends Partial<Subscriptions_SubscriptionRow> {
  subscription_id?: number
  /** Staff entry point: the roster row, rendered with every field + actions. */
  staff?: boolean
  row?: Subscriptions_AdminSubscriptionRow
}

interface CompactRow {
  prop: string
  value: string | number | null
  testId?: string
}

interface DetailGroup {
  title: string
  testId: string
  rows: CompactRow[]
}

const { t, n } = useI18n()
const { rows: allRows } = useMySubscriptions()
const { rows: txRawRows, fetchTransactions } = useSubscriptionTransactions()
const { adminRows, cancelSubscription, processSubscription } = useSubscriptionAdmin()
const { fromApiDateTimeFormat, formatDate, formatDateTime } = useDateFormatter()
const { offsetDaysTotal: testClockOffsetDaysTotal, fetchState: fetchTestClockState } = useTestClock()
const { featureMap: gatewayFeatureMap, loadMap: loadGatewayMap } = usePaymentGatewayRegistry()

const isVisible = ref(false)
const activeTab = ref(0)
const currentRow = ref<ModalPayload>({})
const txFetched = ref(false)
const testClockFetched = ref(false)

// Staff-only state
const isStaff = ref(false)
const staffRow = ref<Subscriptions_AdminSubscriptionRow | null>(null)
const isCancelling = ref(false)
const confirmingCancel = ref(false)
const isProcessing = ref(false)

const modalTitle = computed((): string =>
  isStaff.value ? t('Assinatura') : t('Detalhes da assinatura'),
)

// ── Status helpers (shared with MyFranchisesTable) ────────────────────────────

const statusLabel = (status: string | undefined | null): string => {
  const labels: Record<string, string> = {
    active:     t('Ativa'),
    grace:      t('Carência'),
    cancelled:  t('Cancelada'),
    superseded: t('Substituída'),
    pending:    t('Pendente'),
  }
  if (!status) return t('Sem assinatura')
  return labels[status] ?? status
}

// ── Tab 1: compact key/value rows ─────────────────────────────────────────────

const dadosRows = computed((): CompactRow[] => {
  const row = currentRow.value
  const formatDt = (iso?: string) =>
    iso ? formatDateTime(fromApiDateTimeFormat(iso)) : '—'
  const formatD = (iso?: string) =>
    iso ? formatDate(fromApiDateTimeFormat(iso)) : '—'

  return [
    { prop: t('ID'), value: row.subscription_id ?? '—' },
    { prop: t('Franquia'), value: row.franchise_name ?? '—' },
    { prop: t('Plano'), value: row.plan_name ?? '—' },
    { prop: t('Status'), value: statusLabel(row.status) },
    { prop: t('Isenta'), value: row.exempt ? t('Sim') : t('Não') },
    { prop: t('Carência até'), value: formatD(row.grace_ends_at) },
    { prop: t('Pago até'), value: formatD(row.paid_through) },
    { prop: t('Início do período'), value: formatDt(row.current_period_start) },
    { prop: t('Fim do período'), value: formatDt(row.current_period_end) },
    { prop: t('Criado em'), value: formatDt(row.created_at) },
  ]
})

// ── Staff: every field, grouped ───────────────────────────────────────────────

const EMPTY = '—'

function text(value: string | null | undefined): string {
  return value != null && value !== '' ? value : EMPTY
}

function num(value: number | null | undefined): string {
  return value != null ? String(value) : EMPTY
}

function bool(value: boolean | null | undefined): string {
  if (value == null) return EMPTY
  return value ? t('Sim') : t('Não')
}

function datetime(value: string | null | undefined): string {
  if (!value) return EMPTY
  return formatDateTime(fromApiDateTimeFormat(value))
}

/**
 * Four groups, in the order a staff member reads them: who/what it is, where it
 * stands, what it costs and through whom, and the raw audit trail.
 *
 * Each row carries its own testId so one field stays addressable — the value
 * cells live inside <compact-data-table>, which stamps them onto the value span.
 */
const staffGroups = computed((): DetailGroup[] => {
  const r = staffRow.value
  if (!r) return []

  return [
    {
      title: t('Identificação'),
      testId: 'identificacao',
      rows: [
        { prop: t('ID'),          value: num(r.subscription_id), testId: 'staff-subscription-detail-subscription-id' },
        { prop: t('Franquia'),    value: text(r.franchise_name), testId: 'staff-subscription-detail-franchise-name' },
        { prop: t('ID franquia'), value: num(r.franquia_id),     testId: 'staff-subscription-detail-franquia-id' },
        { prop: t('Nome'),        value: text(r.name),           testId: 'staff-subscription-detail-name' },
        { prop: t('Descrição'),   value: text(r.description),    testId: 'staff-subscription-detail-description' },
      ],
    },
    {
      title: t('Situação'),
      testId: 'situacao',
      rows: [
        { prop: t('Status'),                   value: statusLabel(r.status),            testId: 'staff-subscription-detail-status' },
        { prop: t('Ao vivo'),                  value: bool(r.is_live),                  testId: 'staff-subscription-detail-is-live' },
        { prop: t('Isenta'),                   value: bool(r.exempt),                   testId: 'staff-subscription-detail-exempt' },
        { prop: t('Taxa de adesão paga'),      value: bool(r.setup_paid),               testId: 'staff-subscription-detail-setup-paid' },
        { prop: t('Taxa de implantação'),      value: text(r.setup_status ? (r.setup_status === 'paid' ? t('Paga') : r.setup_status === 'exempt' ? t('Isenta') : t('Pendente')) : t('Pendente')), testId: 'staff-subscription-detail-setup-status' },
        { prop: t('Início'),                   value: datetime(r.started_at),           testId: 'staff-subscription-detail-started-at' },

        { prop: t('Início do período'),        value: datetime(r.current_period_start), testId: 'staff-subscription-detail-current-period-start' },
        { prop: t('Fim do período'),           value: datetime(r.current_period_end),   testId: 'staff-subscription-detail-current-period-end' },
        { prop: t('Pago até'),                 value: datetime(r.paid_through),         testId: 'staff-subscription-detail-paid-through' },
        { prop: t('Carência até'),             value: datetime(r.grace_until),          testId: 'staff-subscription-detail-grace-until' },
        { prop: t('Fim da carência (legado)'), value: datetime(r.grace_period_end),     testId: 'staff-subscription-detail-grace-period-end' },
        { prop: t('Dias restantes'),           value: num(r.days_remaining),            testId: 'staff-subscription-detail-days-remaining' },
      ],
    },
    {
      title: t('Plano e mercado'),
      testId: 'plano',
      rows: [
        { prop: t('Plano'),            value: text(r.plan_name),           testId: 'staff-subscription-detail-plan-name' },
        { prop: t('ID do plano'),      value: num(r.plan_id),              testId: 'staff-subscription-detail-plan-id' },
        { prop: t('ID do preço'),      value: num(r.plan_price_id),        testId: 'staff-subscription-detail-plan-price-id' },
        { prop: t('Moeda'),            value: text(r.currency),            testId: 'staff-subscription-detail-currency' },
        { prop: t('Gateway do preço'), value: text(r.price_provider_code), testId: 'staff-subscription-detail-price-provider-code' },
      ],
    },
    {
      title: t('Gateway'),
      testId: 'gateway',
      rows: [
        { prop: t('Gateway'),          value: text(r.provider),                 testId: 'staff-subscription-detail-provider' },
        { prop: t('ID do cliente'),    value: text(r.provider_customer_id),     testId: 'staff-subscription-detail-provider-customer-id' },
        { prop: t('ID da assinatura'), value: text(r.provider_subscription_id), testId: 'staff-subscription-detail-provider-subscription-id' },
        { prop: t('Criada em'),        value: datetime(r.created_at),           testId: 'staff-subscription-detail-created-at' },
        { prop: t('Atualizada em'),    value: datetime(r.updated_at),           testId: 'staff-subscription-detail-updated-at' },
      ],
    },
  ]
})

/**
 * Cancellable statuses, mirroring
 * SubscriptionCancellationService::CANCELLABLE_STATUSES. `cancelled`/`superseded`
 * are already terminal; calling the gateway again for them would be a pointless
 * remote write.
 *
 * This list only decides whether the BUTTON is offered. The backend refuses
 * independently — the button is a courtesy, never the gate.
 */
const CANCELLABLE_STATUSES = ['pending', 'active', 'grace']

const isCancellable = computed((): boolean => {
  const r = staffRow.value
  if (!r || r.subscription_id == null) return false
  return CANCELLABLE_STATUSES.includes(r.status ?? '')
})

const cancelBlockedReason = computed((): string => {
  const r = staffRow.value
  if (!r) return ''
  if (r.subscription_id == null) return t('Esta franquia não tem assinatura para cancelar.')
  if (r.status === 'cancelled') return t('Assinatura já cancelada.')
  if (r.status === 'superseded') return t('Assinatura substituída por outra — não há o que cancelar.')
  return t('Assinatura não cancelável neste estado.')
})

/**
 * Tab 4 (staff only): the "Relógio" (GatewayTest clock) gate — same signal
 * GatewayTestDevTrigger uses, reused as-is (no fetch of our own here): the
 * feature map is whatever's already loaded elsewhere this session. Presence
 * alone is NOT the signal — production seeds a gatewaytest row unconditionally
 * — only the `enabled` flag is.
 */
const isGatewayTestEnabled = computed((): boolean =>
  !!gatewayFeatureMap.value
  && Object.values(gatewayFeatureMap.value).some((providerMap) => providerMap['gatewaytest']?.enabled === true),
)

const showTestClockTab = computed((): boolean =>
  isStaff.value && staffRow.value?.provider === 'gatewaytest' && isGatewayTestEnabled.value,
)

// ── Tab 2: pre-mapped transaction display rows ────────────────────────────────

// Same vocabulary as BillingThreeSixtyDetail/StaffTransactionModal — the raw
// stored kind ('cycle', 'setup_fee', …) must never leak at the user.
const kindLabelMap: Record<string, string> = {
  cycle: t('Mensalidade'),
  charge: t('Mensalidade'),
  setup_fee: t('Taxa de implantação'),
  credit_purchase: t('Créditos IA'),
}

const txRows = computed(() =>
  txRawRows.value.map((row) => ({
    ...row,
    kind_display: row.kind ? (kindLabelMap[row.kind] ?? row.kind) : '—',
    // row.currency and row.amount_major are set by useSubscriptionTransactions.
    amount_display: n(
      row.amount_major ?? 0,
      { style: 'currency', currency: row.currency ?? 'BRL' },
    ),
    paid_at_display: row.paid_at
      ? formatDateTime(fromApiDateTimeFormat(row.paid_at))
      : row.created_at
        ? formatDateTime(fromApiDateTimeFormat(row.created_at))
        : '—',
    created_at_display: row.created_at
      ? formatDateTime(fromApiDateTimeFormat(row.created_at))
      : '—',
  })),
)

// ── Tab 2: transactions DataTable ─────────────────────────────────────────────

/**
 * Staff only: the tab's rows are the same charges /config/transacoes lists, so
 * the id opens the SAME StaffTransactionModal (one house per entity — the
 * pdv/caixa merge rule). The franqueado keeps a plain id: that modal is a
 * console surface (mark-refunded action, staff vocabulary) and must not leak
 * into the self-service view.
 */
function handleTxIdClick(_value: unknown, row: unknown): void {
  const tx = row as Record<string, unknown>
  eventBus.emit('modal.staffTransaction.open', {
    ...tx,
    franchise_name: staffRow.value?.franchise_name ?? null,
    franquia_id: staffRow.value?.franquia_id ?? null,
  })
}

const transacoesConfig = computed(() => ({
  tableId: 'subscriptionTransactionsTable',
  columns: [
    isStaff.value
      ? { data: 'id', title: t('ID'), renderType: 'custom_clickable_id', clickCallback: handleTxIdClick }
      : { data: 'id', title: t('ID') },
    { data: 'paid_at_display', title: t('Data') },
    { data: 'kind_display', title: t('Tipo') },
    { data: 'currency', title: t('Moeda') },
    { data: 'amount_display', title: t('Valor') },
    { data: 'status', title: t('Status') },
  ],
  pagination: true,
  pageLength: 25,
}))

// ── Tab 3: subscriptions for THIS franchise only (live + history) ─────────────
// Each audience filters the list it ALREADY holds — no new fetch:
//   franqueado — useMySubscriptions (/subscriptions/mine has every row).
//   staff      — useSubscriptionAdmin's roster (admin/list carries history too),
//                keyed by franquia_id and reshaped to the same three columns.
const franchiseRows = computed(() => {
  if (isStaff.value) {
    const fid = staffRow.value?.franquia_id
    if (fid == null) return []
    return adminRows.value
      .filter((r) => r.franquia_id === fid && r.subscription_id != null)
      .map((r) => ({
        franchise_name: r.franchise_name,
        plan_name: r.plan_name,
        status: r.status,
      }))
  }
  return allRows.value.filter((r) => r.franchise_id === currentRow.value.franchise_id)
})

/** The open subscription's id, from whichever row shape the caller handed us. */
const effectiveSubscriptionId = computed((): number | null =>
  (isStaff.value ? staffRow.value?.subscription_id : currentRow.value.subscription_id) ?? null,
)

// ── Tab 3: this-franchise subscriptions DataTable ────────────────────────────

const todasConfig = computed(() => ({
  tableId: 'subscriptionAllTable',
  columns: [
    { data: 'franchise_name', title: t('Franquia') },
    { data: 'plan_name', title: t('Plano') },
    { data: 'status', title: t('Status') },
  ],
  pagination: true,
  pageLength: 25,
}))

// ── Lazy transaction load when Tab 2 is activated ────────────────────────────

watch(activeTab, async (tab) => {
  if (tab === 1 && !txFetched.value && effectiveSubscriptionId.value != null) {
    txFetched.value = true
    try {
      await fetchTransactions(effectiveSubscriptionId.value)
    } catch (err) {
      log('SubscriptionDetailModal', 'fetchTransactions', 'Erro ao carregar transações', err)
      eventBus.emit('globalMessage.error', { message: (err as Error)?.message })
    }
  }
  if (tab === 3 && !testClockFetched.value && effectiveSubscriptionId.value != null) {
    testClockFetched.value = true
    try {
      await fetchTestClockState(effectiveSubscriptionId.value)
    } catch (err) {
      log('SubscriptionDetailModal', 'fetchTestClockState', 'Erro ao carregar estado do relógio', err)
      eventBus.emit('globalMessage.error', { message: (err as Error)?.message })
    }
  }
})

// ── Staff actions ─────────────────────────────────────────────────────────────

/**
 * Estender hands the row to the extend modal and steps aside. Two stacked
 * dialogs would let the staff act on a subscription behind a dialog that is
 * already acting on it.
 */
function openExtend(): void {
  const current = staffRow.value
  if (!current) return
  closeModal()
  eventBus.emit('modal.staffSubscriptionsExtend.open', { row: current })
}

function openSetupFee(): void {
  const current = staffRow.value
  if (!current || !current.franquia_id) return
  closeModal()
  eventBus.emit('modal.setup-fee-emit.open', {
    franquiaId: current.franquia_id,
  })
}

function askCancel(): void {
  confirmingCancel.value = true
}


/**
 * A refusal reaches us as a thrown Error carrying the backend's machine reason
 * (the composable checks the ENVELOPE — HTTP is always 200 here).
 *
 * `retry_scheduled` is NOT a failure: the gateway still has a payment attempt
 * pending, so the cut was deferred rather than executed. It gets a warning tone
 * and its own sentence — reading it as an error would send staff chasing a
 * problem that does not exist. Everything else really is a refusal or an error.
 */
const CANCEL_MESSAGES: Record<string, string> = {
  subscription_not_cancellable: 'Assinatura não pode ser cancelada neste estado.',
  subscription_not_found:       'Assinatura não encontrada.',
  gateway_not_registered:       'O gateway desta assinatura não tem integração — ela não foi cancelada e pode continuar cobrando.',
  gateway_cannot_cancel:        'O gateway desta assinatura não suporta cancelamento — ela não foi cancelada.',
}

function cancelErrorMessage(reason: string): string {
  const known = CANCEL_MESSAGES[reason]
  if (known) return t(known)
  if (reason.startsWith('gateway_cancel_failed')) {
    return t('O gateway recusou o cancelamento. Nada foi alterado aqui — tente de novo.')
  }
  if (reason.startsWith('local_write_failed')) {
    return t('O gateway cancelou, mas a gravação local falhou. A próxima rodada repete o corte.')
  }
  return t('Erro ao cancelar assinatura')
}

async function handleCancel(): Promise<void> {
  const current = staffRow.value
  if (!current || current.subscription_id == null) return

  isCancelling.value = true
  try {
    const dados = await cancelSubscription(current.subscription_id)
    log('SubscriptionDetailModal', 'handleCancel', 'Assinatura cancelada', dados)
    eventBus.emit('globalMessage.success', t('Assinatura cancelada'))
    eventBus.emit('staffSubscription.cancelled', { subscription_id: current.subscription_id })
    closeModal()
    eventBus.emit('staffSubscriptions.refresh')
  } catch (err) {
    const reason = (err as Error)?.message ?? ''
    log('SubscriptionDetailModal', 'handleCancel', 'Cancelamento não concluído', { reason })
    if (reason === 'retry_scheduled') {
      // Deferred, not failed — the system working correctly and saying "not yet".
      eventBus.emit(
        'globalMessage.warning',
        t('Cancelamento adiado: o gateway ainda vai tentar cobrar. A assinatura segue como está e o corte acontece depois da tentativa.'),
      )
    } else {
      eventBus.emit('globalMessage.error', cancelErrorMessage(reason))
    }
    confirmingCancel.value = false
  } finally {
    isCancelling.value = false
  }
}

/**
 * "Processar agora" asks the sweep what it does with THIS row today, and every
 * answer arrives in a success envelope — the mechanism ran and is reporting.
 * The tone therefore comes from `dados.outcome`, never from the envelope:
 *
 *   not_due            — the carência has not closed yet. Nothing to do, and
 *                        nothing wrong: an info toast, not a failure.
 *   cancelled          — the cut happened. Same closing behaviour as Cancelar.
 *   deferred           — the gateway still intends to charge, so the cut waits.
 *                        Same sentence and same warning tone as a deferred cancel.
 *   refused /
 *   gateway_failed /
 *   local_write_failed — a real refusal or error, with the backend's own reason.
 *
 * A THROWN error here is a different animal: the round never ran at all (auth,
 * an unknown id, an exception), checked on the envelope by the composable.
 */
async function handleProcess(): Promise<void> {
  const id = effectiveSubscriptionId.value
  if (id == null) return

  isProcessing.value = true
  try {
    const dados = await processSubscription(id)
    log('SubscriptionDetailModal', 'handleProcess', 'Assinatura processada', dados)
    const reason = dados.reason ?? ''

    switch (dados.outcome) {
      case 'not_due':
        // The status is concatenated OUTSIDE t(): unregistered keys come back
        // verbatim from the translation lookup, so a '{status}' placeholder would
        // reach the staff literally.
        eventBus.emit('globalMessage.show', {
          message: `${t('Nada a fazer: a carência ainda não venceu.')} ${t('Status')}: ${statusLabel(dados.status)}`,
          type: 'info',
        })
        break

      case 'cancelled':
        eventBus.emit('globalMessage.success', t('Assinatura cancelada'))
        eventBus.emit('staffSubscription.cancelled', { subscription_id: id })
        closeModal()
        eventBus.emit('staffSubscriptions.refresh')
        break

      case 'deferred':
        eventBus.emit(
          'globalMessage.warning',
          t('Cancelamento adiado: o gateway ainda vai tentar cobrar. A assinatura segue como está e o corte acontece depois da tentativa.'),
        )
        break

      default:
        eventBus.emit('globalMessage.error', cancelErrorMessage(reason))
    }
  } catch (err) {
    const reason = (err as Error)?.message ?? ''
    log('SubscriptionDetailModal', 'handleProcess', 'Processamento não executado', { reason })
    eventBus.emit('globalMessage.error', `${t('Não foi possível processar esta assinatura')}: ${reason}`)
  } finally {
    isProcessing.value = false
  }
}

/**
 * The Relógio tab reacts to its OWN refresh, not the roster's: 'staffSubscriptions.refresh'
 * tells the LIST behind the modal to re-fetch, which does nothing for the row
 * already open here. Re-reading the clock state is enough to keep the badge
 * and the history current while the modal stays open.
 */
function handleTestClockRefresh(): void {
  eventBus.emit('staffSubscriptions.refresh')
}

// ── Modal lifecycle ───────────────────────────────────────────────────────────

function onOpen(payload: ModalPayload): void {
  log('SubscriptionDetailModal', 'onOpen', 'Abrindo detalhe de assinatura', payload)
  const data = payload ?? {}
  // Defense in depth: the staff view (full field set + staff actions) needs
  // BOTH the caller's intent AND real Full membership — a payload flag alone
  // must never grant it (the user 360 once passed staff:true unconditionally).
  isStaff.value = data.staff === true
    && (permissionsState.user?.groups ?? []).includes('Full')
  staffRow.value = data.row ?? null
  currentRow.value = data
  activeTab.value = 0
  txFetched.value = false
  testClockFetched.value = false
  confirmingCancel.value = false
  isCancelling.value = false
  isProcessing.value = false
  isVisible.value = true

  // featureMap é singleton de módulo e nasce null: sem esta chamada, a aba do
  // Relógio só aparecia para quem tivesse passado por /config/payment-gateways
  // antes, na mesma sessão de página — recarregar o navegador a fazia sumir.
  // Só para staff, que é quem tem a aba.
  if (isStaff.value) {
    loadGatewayMap().catch((err) => {
      log('SubscriptionDetailModal', 'onOpen', 'Erro ao carregar mapa de gateways', err)
    })
  }
}

function closeModal(): void {
  log('SubscriptionDetailModal', 'closeModal', 'Fechando detalhe de assinatura')
  isVisible.value = false
  currentRow.value = {}
  staffRow.value = null
  isStaff.value = false
  confirmingCancel.value = false
  eventBus.emit('modal.subscriptionDetail.close')
}

onMounted(() => {
  eventBus.on('modal.subscriptionDetail.open', onOpen as (data?: unknown) => void)
})

onBeforeUnmount(() => {
  eventBus.off('modal.subscriptionDetail.open', onOpen as (data?: unknown) => void)
})
</script>

<style scoped>
.actions-section {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 1rem;
}

/* Above the tabs the section needs breathing room BELOW (toward the TabList)
   and none above — the modal header already provides the top spacing. */
.actions-section--top {
  margin-top: 0;
  margin-bottom: 1rem;
}

.confirm-text {
  margin: 0 0 0.75rem;
}
</style>
